import React, { useState, useEffect, useCallback } from 'react';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { BrowserRouter as Router, Route, Routes, Navigate, useNavigate, useLocation, Link } from 'react-router-dom';
// import { HashRouter as Router, Route, Routes, Navigate, useNavigate, useLocation } from 'react-router-dom';
import { Button } from "./components/button"
import { ThemeProvider } from "./components/theme-provider"
import { Toaster } from "./components/toaster"
import { useToast } from "./components/use-toast"
import { motion, AnimatePresence } from "framer-motion"
import AuthComponent from './components/authcomponent';
import Dashboard from './components/dashboard';
import PaymentSuccess from './components/PaymentSuccess';
import Playground from './components/playground';
import { LogOut, Loader2, ArrowLeft, History, Unlock, User, Ticket, Zap } from 'lucide-react'
import VouchersDialog from './components/VouchersDialog';
import PublicBuyVoucherDialog from './components/PublicBuyVoucherDialog';
import WebhookStylePicker from './components/WebhookStylePicker';
import { Popover, PopoverContent, PopoverTrigger } from './components/popover'
import DiscountBanner from './components/discountbanner';
import { trackAuthEvents } from './utils/ga4Events';

import { Contact, Impressum, Terms, Refund, Privacy, About } from './components/pages/Pages';
import CookieBanner from './components/CookieBanner';
import { LanguageProvider } from './components/LanguageToggle';
import Footer from './components/Footer';
import ReviewsPage from './components/ReviewsPage';
import ReviewsList from './components/ReviewsList';
import ReviewForm from './components/ReviewForm';

import logo from './assets/logo.png'
import image1 from './assets/example1.png';
import image2 from './assets/example2.png';
import image3 from './assets/example3.png';
import image4 from './assets/example4.png';
import image5 from './assets/example5.png';
import image6 from './assets/example6.png';
import image7 from './assets/example7.png';

import { trackPlaygroundEvents } from './utils/ga4Events';

const API_URL = process.env.REACT_APP_API_URL ? `${process.env.REACT_APP_API_URL}/api` : '/api';

// GoogleOAuthProvider is wrapped around AuthComponent only (instead of the
// whole app) so that @react-oauth/google injects the gsi/client script —
// which contacts Google — only when the user navigates to a login page.
const AuthWithGoogle = (props) => (
  <GoogleOAuthProvider clientId={process.env.REACT_APP_GOOGLE_CLIENT_ID}>
    <AuthComponent {...props} />
  </GoogleOAuthProvider>
);

const Slideshow = () => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const images = [
    image1, image2, image3, image4, image5, image6, image7
  ];

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentImageIndex((prevIndex) =>
        prevIndex === images.length - 1 ? 0 : prevIndex + 1
      );
    }, 6000);

    return () => clearInterval(timer);
  }, []);

  return (
    <div className="w-full mx-auto overflow-hidden relative aspect-[4/3]">
      {images.map((src, index) => (
        <div
          key={index}
          className={`absolute inset-0 transition-all duration-1000 ease-in-out ${index === currentImageIndex ? 'opacity-100' : 'opacity-0'
            }`}
        >
          <img
            src={src}
            alt={`Example print`}
            className="w-full h-full p-2 object-contain"
          />
        </div>
      ))}
    </div>
  );
};

// Landing page strip: 3 most recent approved reviews + a link to the full
// public list. Returns null on empty / error so the landing page stays clean
// before any reviews exist (or while the request is in flight).
const LandingReviewsStrip = () => {
  const [reviews, setReviews] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_URL}/reviews?limit=3`);
        if (!r.ok) return;
        const data = await r.json();
        if (!cancelled) setReviews(data.reviews || []);
      } catch (_e) { /* swallow — landing page must still render */ } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!loaded || reviews.length === 0) return null;
  return (
    <div className="w-full">
      <h3 className="text-lg lg:text-xl font-semibold text-center mb-4">What people are saying</h3>
      <ReviewsList reviews={reviews} columns="sm:grid-cols-2 lg:grid-cols-3" />
      <div className="flex justify-center mt-4">
        <Link to="/reviews" className="text-sm text-gray-700 underline hover:text-gray-900">
          View all reviews →
        </Link>
      </div>
    </div>
  );
};

const LandingPage = ({ onLoginClick, onDemoClick, onBuyVoucherClick }) => {
  return (
    <div className="w-full">
      <div className="container mx-auto px-4 max-w-4xl">
        <div className="flex flex-col items-center justify-center gap-8 pb-12">
          {/* <h2 className="text-xl lg:text-3xl font-bold text-center mt-8">Get your personalized map </h2> */}
          <h2 className="text-xl lg:text-3xl font-bold text-center mt-8">Create customizable and personalized maps! </h2>

          <div className="w-full">
            <Slideshow />
          </div>
          <div className="flex flex-col items-center justify-center space-y-6 w-full max-w-2xl">
            {/* <p className="text-md text-gray-600 text-center">
              Create customizable and personalized maps!
            </p> */}
            <div className="flex flex-col gap-4 w-full items-center">
              <Button size="lg" className="w-64 rounded-2xl bg-gray-900 text-white border-0 hover:bg-gray-600 shadow-2xl" onClick={onLoginClick}>
                Get Your Personal Map
              </Button>
              <Button size="lg" variant="outline" className="w-64 rounded-2xl border-2 border-gray-900 text-gray-900 hover:bg-gray-100" onClick={onBuyVoucherClick}>
                <Ticket className="mr-2 h-4 w-4" /> Buy a Voucher
              </Button>
              <Button size="lg" variant="outline" className="w-64 rounded-2xl border-2 border-gray-900 text-gray-900 hover:bg-gray-100" onClick={onDemoClick}>
                Try Demo Playground
              </Button>
            </div>
          </div>
          <LandingReviewsStrip />
        </div>
      </div>
    </div>
  );
};

function AppContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const { toast } = useToast();
  const [globalSelectedTab, setGlobalSelectedTab] = useState("create");
  const [showBanner, setShowBanner] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [autoGenerate, setAutoGenerate] = useState(false);
  // Webhook style pin: a free-form label (template/style name) the user
  // last picked, and a flag indicating whether the backend holds a settings
  // dict. Empty label + flag=false means "use a random style each time".
  const [webhookStyleLabel, setWebhookStyleLabel] = useState('');
  const [hasWebhookSettings, setHasWebhookSettings] = useState(false);
  const [webhookPickerOpen, setWebhookPickerOpen] = useState(false);
  // Controlled so action buttons inside the popover (Leave a review, Logout)
  // can collapse it themselves — Radix doesn't auto-close on inner clicks.
  const [profilePopoverOpen, setProfilePopoverOpen] = useState(false);
  // Review dialog is hoisted out of the popover so closing the popover
  // doesn't unmount the dialog before it gets a chance to render.
  const [reviewOpen, setReviewOpen] = useState(false);
  const [vouchersOpen, setVouchersOpen] = useState(false);
  const [publicBuyVoucherOpen, setPublicBuyVoucherOpen] = useState(false);
  // Count of newly-purchased maps the user hasn't viewed yet. Persisted to
  // localStorage so badge survives reloads, and synced across tabs via the
  // 'storage' event so a purchase in one window updates the badge in another.
  const [unseenMaps, setUnseenMaps] = useState(() => {
    try {
      const v = parseInt(localStorage.getItem('unseenMaps') || '0', 10);
      return Number.isNaN(v) ? 0 : v;
    } catch (_e) { return 0; }
  });
  // Server-side "newest webhook image timestamp" pulled with the rest of
  // user/status. Treated as the source of truth — `lastSeenWebhookAt` in
  // localStorage records the user's last visit to the webhook view, and
  // `hasUnseenWebhook` is the comparison result. Same logic for paid.
  const [latestWebhookAt, setLatestWebhookAt] = useState(null);
  const [latestPaidAt, setLatestPaidAt] = useState(null);
  const [lastSeenWebhookAt, setLastSeenWebhookAt] = useState(() => {
    try { return localStorage.getItem('lastSeenWebhookAt') || ''; } catch (_e) { return ''; }
  });
  const [lastSeenPaidAt, setLastSeenPaidAt] = useState(() => {
    try { return localStorage.getItem('lastSeenPaidAt') || ''; } catch (_e) { return ''; }
  });
  // Stamped by Dashboard when the user actually views a sub-tab. Persists
  // to localStorage so the badge stays cleared across reloads.
  const ackWebhookSeen = useCallback(() => {
    if (!latestWebhookAt) return;
    setLastSeenWebhookAt(latestWebhookAt);
    try { localStorage.setItem('lastSeenWebhookAt', latestWebhookAt); } catch (_e) { /* no-op */ }
  }, [latestWebhookAt]);
  const ackPaidSeen = useCallback(() => {
    if (!latestPaidAt) return;
    setLastSeenPaidAt(latestPaidAt);
    try { localStorage.setItem('lastSeenPaidAt', latestPaidAt); } catch (_e) { /* no-op */ }
  }, [latestPaidAt]);

  useEffect(() => {
    const readUnseen = () => {
      try {
        const v = parseInt(localStorage.getItem('unseenMaps') || '0', 10);
        setUnseenMaps(Number.isNaN(v) ? 0 : v);
      } catch (_e) { /* no-op */ }
    };
    const onStorage = (e) => {
      // 'storage' only fires in OTHER tabs. The same-tab refresh path
      // is the custom 'unseenMapsChanged' event below.
      if (e.key !== 'unseenMaps') return;
      readUnseen();
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener('unseenMapsChanged', readUnseen);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('unseenMapsChanged', readUnseen);
    };
  }, []);

  // Clear the badge whenever the user is actually viewing the history tab —
  // covers direct navigation, goToTab, and the post-payment auto-redirect.
  // Defensive on the localStorage write too: PaymentSuccess writes the
  // counter in the same tab (no 'storage' event), so even when our state
  // reads as 0 the persisted value may be >0; always sync both.
  useEffect(() => {
    if (globalSelectedTab !== 'history') return;
    if (unseenMaps > 0) setUnseenMaps(0);
    try {
      const cur = localStorage.getItem('unseenMaps');
      if (cur && cur !== '0') localStorage.setItem('unseenMaps', '0');
    } catch (_e) { /* no-op */ }
  }, [globalSelectedTab, unseenMaps]);

  // Pull the webhook auto-generate flag alongside auth so the profile toggle
  // reflects the server state on load, not just after a refetch.
  useEffect(() => {
    if (!user) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch(`${API_URL}/user/status`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!data) return;
        setAutoGenerate(!!data.auto_generate_on_webhook);
        setIsAdmin(!!data.is_admin);
        setHasWebhookSettings(!!data.has_webhook_settings);
        setWebhookStyleLabel(data.webhook_style_label || '');
        setLatestWebhookAt(data.latest_webhook_image_at || null);
        setLatestPaidAt(data.latest_paid_image_at || null);
      })
      .catch(() => { });
  }, [user]);

  // Derive unseen flags by comparing server-side latest timestamps with
  // the per-kind lastSeen value held in state (and mirrored to localStorage).
  // Cheap, runs on each render — both sides are tiny ISO strings.
  const hasUnseenWebhook = !!(latestWebhookAt && latestWebhookAt > lastSeenWebhookAt);
  const hasUnseenPaid = !!(latestPaidAt && latestPaidAt > lastSeenPaidAt);

  // The "Your Maps" header button shows the badge if EITHER kind has
  // unseen content. `unseenMaps` is the legacy purchase-counter (same
  // tab + cross-tab), unseenPaid/Webhook are the timestamp-derived
  // signals — they overlap for purchases (PaymentSuccess bumps the
  // counter AND a new paid image lands) but the timestamp path covers
  // the webhook flow too.
  const yourMapsBadge = unseenMaps > 0 || hasUnseenWebhook || hasUnseenPaid;

  const saveWebhookStyle = async ({ settings, label }) => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const prevHas = hasWebhookSettings;
    const prevLabel = webhookStyleLabel;
    // Optimistic update so the trigger button reflects the pick immediately.
    setHasWebhookSettings(settings !== null);
    setWebhookStyleLabel(settings === null ? '' : (label || ''));
    try {
      const r = await fetch(`${API_URL}/user/webhook-style`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ settings, label }),
      });
      if (!r.ok) throw new Error('update failed');
    } catch {
      setHasWebhookSettings(prevHas);
      setWebhookStyleLabel(prevLabel);
      toast({
        title: 'Error',
        description: "Couldn't update the webhook style.",
        variant: 'destructive',
        duration: 3000,
      });
    }
  };

  const toggleAutoGenerate = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const next = !autoGenerate;
    setAutoGenerate(next);
    try {
      const r = await fetch(`${API_URL}/user/auto-generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ enabled: next }),
      });
      if (!r.ok) throw new Error('update failed');
    } catch {
      setAutoGenerate(!next);
      toast({
        title: 'Error',
        description: "Couldn't update the auto-generate setting.",
        variant: 'destructive',
        duration: 3000,
      });
    }
  };

  const goToTab = (tab) => {
    setGlobalSelectedTab(tab);
    // Mirror the tab into the URL so the active view is deep-linkable
    // (e.g. /dashboard?tab=history for the "Your Maps" panel) and the
    // back/forward buttons restore the right tab.
    const params = new URLSearchParams(
      location.pathname === '/dashboard' ? location.search : ''
    );
    params.set('tab', tab);
    const target = `/dashboard?${params.toString()}`;
    if (location.pathname !== '/dashboard') {
      navigate(target);
    } else {
      navigate(target, { replace: true });
    }
  };

  const renderHeaderButton = () => {
    if (user) {
      return (
        <div className="flex items-center gap-1 lg:gap-2">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => goToTab('history')}
            title="Your Maps"
            className="relative rounded-2xl text-gray-700 hover:bg-gray-100 flex items-center gap-1 lg:gap-2"
          >
            <History className="h-4 w-4" />
            <span className="text-xs lg:text-sm">Your Maps</span>
            {yourMapsBadge && (
              <span className="absolute -top-1 -right-1 min-w-[1.125rem] h-[1.125rem] px-1 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center shadow-sm">
                {unseenMaps > 0
                  ? (unseenMaps > 9 ? '9+' : unseenMaps)
                  : ''}
              </span>
            )}
          </Button>
          {isAdmin && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => goToTab('admin')}
              title="Admin Panel"
              className="rounded-2xl text-gray-700 hover:bg-gray-100 h-8 w-8 p-0 flex items-center justify-center"
            >
              <Unlock className="h-4 w-4" />
            </Button>
          )}
          <Popover open={profilePopoverOpen} onOpenChange={setProfilePopoverOpen}>
            <PopoverTrigger asChild>
              <button
                type="button"
                title="Profile"
                className="h-8 w-8 rounded-full bg-gray-900 text-white flex items-center justify-center hover:bg-gray-700"
              >
                <User className="h-4 w-4" />
              </button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-72 z-[2000] bg-white border-gray-200">
              <div className="space-y-3">
                <div>
                  <div className="text-xs text-gray-500">Signed in as</div>
                  <div className="text-sm font-medium break-all">{user.email}</div>
                </div>
                <div className="pt-2 border-t space-y-2">
                  {/* Plain button — the dialog itself lives at app root so
                      closing the popover doesn't unmount the dialog before
                      it has a chance to mount. */}
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full rounded-2xl"
                    onClick={() => { setProfilePopoverOpen(false); setWebhookPickerOpen(true); }}
                  >
                    <Zap className="mr-2 h-4 w-4" /> Auto-generate maps upon new activity for free
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full rounded-2xl"
                    onClick={() => { setProfilePopoverOpen(false); setVouchersOpen(true); }}
                  >
                    <Ticket className="mr-2 h-4 w-4" /> Vouchers
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full rounded-2xl"
                    onClick={() => { setProfilePopoverOpen(false); setReviewOpen(true); }}
                  >
                    Leave a review
                  </Button>
                  <Button
                    size="sm"
                    className="w-full rounded-2xl bg-gray-900 text-white border-0 hover:bg-gray-600"
                    onClick={() => { setProfilePopoverOpen(false); handleLogout(); }}
                  >
                    <LogOut className="mr-2 h-4 w-4" /> Logout
                  </Button>
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      );
    }

    if (location.pathname === '/login') {
      return (
        <Button
          size="sm"
          className="rounded-2xl bg-gray-900 text-white border-0 hover:bg-gray-600"
          onClick={() => navigate('/')}
        >
          <ArrowLeft className="mr-2 h-4 w-4" /> Back
        </Button>
      );
    }

    return (
      <Button
        className="rounded-2xl bg-gray-900 text-white border-0 hover:bg-gray-600"
        size="sm"
        onClick={() => navigate("/login")}
      >
        Login / Register
      </Button>
    );
  };

  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('token');
      if (!token) {
        setIsLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_URL}/protected`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (response.ok) {
          const data = await response.json();
          setUser(data.user);
        } else {
          localStorage.removeItem('token');
          setUser(null);
        }
      } catch (error) {
        toast({
          title: "Error",
          description: "An error occured",
          variant: "destructive",
          duration: 3000,
        });
        localStorage.removeItem('token');
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, []);

  const handleLogin = (userData, token) => {
    setUser(userData);
    localStorage.setItem('token', token);
    toast({
      title: "Welcome!",
      description: "You've successfully logged in.",
      duration: 2000,
    });
  };

  const handleLogout = () => {
    trackAuthEvents.logout();
    setUser(null);
    setIsAdmin(false);
    setAutoGenerate(false);
    localStorage.removeItem('token');
    toast({
      title: "Logged out",
      description: "You've been successfully logged out.",
      duration: 2000
    });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-orange-100 to-orange-0 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  return (

    <LanguageProvider>
      <div className="flex flex-col min-h-screen">
        {/* <header className="bg-white/80 backdrop-blur-sm shadow-md sticky top-0 z-10"> */}
        <header className="h-12 lg:h-16 bg-white/80 backdrop-blur-sm shadow-xl">
          <div className="container mx-auto px-3 py-2 lg:py-4 flex justify-between items-center w-full">
            <h1 className="flex items-center">
              <a href="https://mapprints.de" className="hover:opacity-80 transition-opacity">
                <img src={logo} alt="Map Prints Logo" className="h-6 lg:h-8" />
              </a>
            </h1>
            {renderHeaderButton()}
          </div>
        </header>
        {/* {showBanner && <DiscountBanner onClose={() => setShowBanner(false)} />} */}
        <Toaster className="fixed !bottom-0 !top-auto md:!bottom-4" />
        {/* <main className="flex-1 flex flex-col h-[calc(100vh-8rem)]"> */}
        <main className="flex-1 flex flex-col">
          <AnimatePresence mode="wait">
            <Routes>
              <Route
                path="/"
                element={
                  user ? (
                    <Navigate to="/dashboard" replace />
                  ) : (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.3 }}
                      className="flex-1 flex items-center justify-center"
                    >
                      <LandingPage
                        onLoginClick={() => navigate("/login")}
                        onDemoClick={async () => {
                          await Promise.resolve(trackPlaygroundEvents.playgroundEntered());
                          navigate("/playground");
                        }}
                        onBuyVoucherClick={() => setPublicBuyVoucherOpen(true)}
                      />
                    </motion.div>
                  )
                }
              />
              <Route
                path="/login"
                element={
                  user ? (
                    <Navigate to="/dashboard" replace />
                  ) : (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.3 }}
                      className="flex-1 flex items-center justify-center"
                    >
                      <AuthWithGoogle onLogin={handleLogin} />
                    </motion.div>
                  )
                }
              />
              <Route
                path="/verify-email"
                element={
                  user ? (
                    <Navigate to="/dashboard" replace />
                  ) : (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.3 }}
                      className="flex-1 flex items-center justify-center"
                    >
                      <AuthWithGoogle onLogin={handleLogin} isVerification={true} />
                    </motion.div>
                  )
                }
              />
              <Route
                path="/reset-password"
                element={
                  user ? (
                    <Navigate to="/dashboard" replace />
                  ) : (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.3 }}
                      className="flex-1 flex items-center justify-center"
                    >
                      <AuthWithGoogle onLogin={handleLogin} />
                    </motion.div>
                  )
                }
              />
              <Route
                path="/auth/magic"
                element={
                  user ? (
                    <Navigate to="/dashboard" replace />
                  ) : (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.3 }}
                      className="flex-1 flex items-center justify-center"
                    >
                      <AuthWithGoogle onLogin={handleLogin} />
                    </motion.div>
                  )
                }
              />
              <Route
                path="/dashboard"
                element={
                  !user ? (
                    <Navigate to="/" replace />
                  ) : (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.3 }}
                      className="flex-1 h-full"
                    // className='flex-1 flex items-center justify-center h-full'
                    >
                      <Dashboard
                        userData={user}
                        onLogout={handleLogout}
                        selectedTab={globalSelectedTab}
                        setSelectedTab={setGlobalSelectedTab}
                        onAdminStatus={setIsAdmin}
                        latestWebhookAt={latestWebhookAt}
                        latestPaidAt={latestPaidAt}
                        hasUnseenWebhook={hasUnseenWebhook}
                        hasUnseenPaid={hasUnseenPaid}
                        lastSeenWebhookAt={lastSeenWebhookAt}
                        lastSeenPaidAt={lastSeenPaidAt}
                        ackWebhookSeen={ackWebhookSeen}
                        ackPaidSeen={ackPaidSeen}
                      />
                    </motion.div>
                  )
                }
              />
              <Route
                path="/playground"
                element={
                  user ? (
                    <Navigate to="/dashboard" replace />
                  ) : (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      transition={{ duration: 0.3 }}
                      className="flex-1 flex flex-col"
                    >
                      <Playground onNavigateToLogin={() => navigate("/login")} />
                    </motion.div>
                  )
                }
              />
              <Route
                path="/payment-success"
                element={
                  // Public voucher buyers land here too — no login required.
                  // PaymentSuccess.js bounces back to / itself when there is
                  // no session_id, so leaving this open is safe.
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    transition={{ duration: 0.3 }}
                    className="flex-1 flex items-center justify-center"
                  >
                    <PaymentSuccess setSelectedTab={setGlobalSelectedTab} />
                  </motion.div>
                }
              />
              <Route
                path="/payment-cancelled"
                element={
                  <Navigate to="/dashboard" replace />
                }
              />
              <Route path="/about" element={<About />} />
              <Route path="/contact" element={<Contact />} />
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<Privacy />} />
              <Route path="/refund" element={<Refund />} />
              <Route path="/impressum" element={<Impressum />} />
              <Route path="/reviews" element={<ReviewsPage />} />
            </Routes>
          </AnimatePresence>
        </main>

        <Footer />
        <CookieBanner />
        {/* Mounted at app root so the popover button can open it without
            keeping the popover itself alive (see profilePopoverOpen). */}
        <ReviewForm open={reviewOpen} onOpenChange={setReviewOpen} />
        <VouchersDialog open={vouchersOpen} onOpenChange={setVouchersOpen} />
        <PublicBuyVoucherDialog open={publicBuyVoucherOpen} onOpenChange={setPublicBuyVoucherOpen} />
        <WebhookStylePicker
          open={webhookPickerOpen}
          onOpenChange={setWebhookPickerOpen}
          autoGenerate={autoGenerate}
          onToggleAutoGenerate={toggleAutoGenerate}
          currentLabel={hasWebhookSettings ? webhookStyleLabel : ''}
          onSave={(settings, label) => saveWebhookStyle({ settings, label })}
          onClear={() => saveWebhookStyle({ settings: null, label: '' })}
        />
      </div>
    </LanguageProvider>
  );
}

export default function App() {
  return (
    <ThemeProvider defaultTheme="light" storageKey="vite-ui-theme">
      <Router>
        <AppContent />
      </Router>
      <Toaster />
    </ThemeProvider>
  );
}